import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifyError, notifyInfo } from '../../../utils/notify';
import {
  ButtonWrapper,
  DeleteButton,
  DropboxPathRow,
  EmptyState,
  FolderCheckbox,
  FolderInfo,
  FolderItem,
  FolderLeft,
  FolderMediaCount,
  FolderName,
  FolderPath,
  FolderSelectionWrapper,
  Form,
  Line,
  SelectedCount,
  SubmitButton,
} from './UploadMediaFromDropboxModal.styles';
import { uploadMediaFromDropbox } from '../../../store/slices/user/asyncThunks';
import Input from '../../utils/inputs/Input/Input';
import BaseModal from '../utils/BaseModal/BaseModal';
import { selectUserActionsPending } from '../../../store/slices/user/slice';

export const uploadMediaFromDropboxSchema = yup
  .object({
    dropboxPath: yup.string().trim().required('Dropbox path is required'),
  })
  .required();

const UploadMediaFromDropboxModal = ({ modalRef, influencerId, onSyncStarted }) => {
  const { uploadMediaFromDropbox: uploadMediaFromDropboxPending } = useSelector(selectUserActionsPending);
  const dispatch = useDispatch();
  const [folders, setFolders] = useState([]);
  const [skippedFilesArr, setSkippedFilesArr] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      dropboxPath: '',
    },
    delayError: 300,
    resolver: yupResolver(uploadMediaFromDropboxSchema),
  });

  const dropboxPath = watch('dropboxPath');
  const isFolderSelectionStep = folders.length > 0;

  const resetModalState = () => {
    reset();
    setFolders([]);
    setSkippedFilesArr([]);
  };

  const handleFolderToggle = path => {
    setFolders(prev => prev.map(folder => (folder.path === path ? { ...folder, checked: !folder.checked } : folder)));
  };

  const selectedFolders = useMemo(() => folders.filter(folder => folder.checked), [folders]);

  const onSubmit = data => {
    const { dropboxPath } = data;

    if (!isFolderSelectionStep) {
      dispatch(uploadMediaFromDropbox({ dropboxPath, influencerId }))
        .unwrap()
        .then(({ folders, skippedFiles }) => {
          const foldersWithChecked = folders.map(folder => ({
            ...folder,
            checked: false,
          }));

          if (!foldersWithChecked.length) {
            notifyInfo('No folders found.');
          }

          setFolders(foldersWithChecked);
          setSkippedFilesArr(skippedFiles);
        })
        .catch(err => {
          notifyError(err.message);
        });

      return;
    }

    dispatch(
      uploadMediaFromDropbox({
        dropboxPath,
        influencerId,
        folders,
      }),
    )
      .unwrap()
      .then(response => {
        resetModalState();
        modalRef.current.hide();
        notifyInfo(response.message);
        onSyncStarted?.();
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <BaseModal
      ref={modalRef}
      title="Upload Media from Dropbox"
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        resetModalState();
      }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {!isFolderSelectionStep && (
          <Input
            label="Dropbox path"
            placeholder="Enter dropbox path"
            {...register('dropboxPath')}
            error={errors.dropboxPath?.message}
          />
        )}
        {isFolderSelectionStep && (
          <FolderSelectionWrapper>
            <DropboxPathRow>
              <strong>Dropbox path:</strong> {dropboxPath}
            </DropboxPathRow>
            {folders.map(folder => (
              <FolderItem key={folder.path}>
                <FolderLeft>
                  <FolderCheckbox
                    type="checkbox"
                    checked={!!folder.checked}
                    onChange={() => handleFolderToggle(folder.path)}
                  />
                  <FolderInfo>
                    <FolderName>{folder.name}</FolderName>
                    <FolderPath>{folder.path}</FolderPath>
                  </FolderInfo>
                </FolderLeft>
                <FolderMediaCount>
                  Media count: <strong>{folder.mediaCount}</strong>
                </FolderMediaCount>
              </FolderItem>
            ))}
            {!folders.length && <EmptyState>No folders found.</EmptyState>}
            <SelectedCount>
              Selected: <strong>{selectedFolders.length}</strong>
            </SelectedCount>
            {!!skippedFilesArr.length && (
              <>
                <Line />
                <DropboxPathRow>
                  <strong>Files that will not be processed:</strong>
                </DropboxPathRow>
                {skippedFilesArr.map(file => (
                  <FolderItem key={file.path}>
                    <FolderLeft>
                      <FolderInfo>
                        <FolderName>{file.path}</FolderName>
                        <FolderPath>{file.reason}</FolderPath>
                      </FolderInfo>
                    </FolderLeft>
                  </FolderItem>
                ))}
              </>
            )}
          </FolderSelectionWrapper>
        )}
        <ButtonWrapper>
          <DeleteButton
            title="Close"
            onClick={() => modalRef.current.hide()}
            type="button"
            disabled={uploadMediaFromDropboxPending}
          />
          <SubmitButton
            type="submit"
            title={isFolderSelectionStep ? 'Upload Media' : 'Submit'}
            isLoading={uploadMediaFromDropboxPending}
          />
        </ButtonWrapper>
      </Form>
    </BaseModal>
  );
};

export default UploadMediaFromDropboxModal;
